import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import useAxiosPrivate from 'hooks/useAxiosPrivate';

import { openAxios } from 'services/axios';
import { PRINT } from 'services/consts';

const initialState = {
	printLocale: 'engValue',
	isUserSelectedLocale: false,
	isSettingsFetchedFromRemote: false,
	invoicePrintPropJSON: {
		title: 'Order Details',
		titleArabic: 'فاتورة',
		templateName: 'template1',
		pageSize: 'A4',
		fontSize: '10px',
		fontWeight: 'regular',
		orderNumberBarcodeFormat: 'CODE128',
		barcodeSize: 'default',
		language: 'engValue',
		logoType: 'companyLogo',
		showQrCode: false,
		showBarcode: true,
		showOrderNumber: true,
		showOrderDate: true,
		showInvoiceCreatedDate: false,
		showDateOfShipmentArrival: false,
		showDeliveryCompany: true,
		showShipmentNumber: true,
		showPaymentMethod: true,
		showVatNumber: true,
		showCRNumber: true,
		showFullName: true,
		showPhoneNumber: true,
		showAddress: true,
		showCity: false,
		showCountry: false,
		showSenderName: true,
		showSenderAddress: false,
		showShippingDescription: true,
		showOrderItems: true,
		showTotalQuantity: false,
		showSubTotal: false,
		showDiscount: false,
		showCodFee: false,
		showShipFee: true,
		showShippingDiscount: false,
		showVatAmount: true,
		showTotalDue: true,
		showGrandTotal: true,
		showOrderComment: false,
		showCouponCode: false,
		columnSettings: [],
		showRef2: false
	},
	branchPrintPropJSON: {
		title: 'Order Details',
		templateName: 'template1',
		pageSize: 'A6',
		fontSize: '10px',
		fontWeight: 'regular',
		orderNumberBarcodeFormat: 'CODE128',
		barcodeSize: 'default',
		language: 'engValue',
		showQrCode: false,
		showBarcode: true,
		showOrderNumber: true,
		showOrderDate: true,
		showInvoiceCreatedDate: false,
		showDateOfShipmentArrival: false,
		showDeliveryCompany: true,
		showShipmentNumber: true,
		showPaymentMethod: true,
		showVatNumber: true,
		showCRNumber: true,
		showFullName: true,
		showPhoneNumber: true,
		showAddress: true,
		showCity: false,
		showSenderName: true,
		showSenderAddress: false,
		showShippingDescription: false,
		showOrderItems: true,
		showTotalQuantity: false,
		showSubTotal: false,
		showDiscount: false,
		showCodFee: false,
		showShipFee: true,
		showVatAmount: true,
		showTotalDue: true,
		showGrandTotal: true,
		showOrderComment: false,
		showCouponCode: false,
		columnSettings: []
	},
	awbPrintPropJSON: {
		showOrderItems: true,
		showOrderNumber: true,
		showOrderNumberBarcode: true,
		showOrderDate: true,
		showInvoiceCreatedDate: true,
		showPaymentMethod: true,
		showSenderAddress: true,
		showAddress: true,
		showDescription: true,
		showGrandTotal: true,
		showTotalDue: true,
		showShipmentDate: true,
		showShipmentNumber: true,
		showSerialNumber: false,
		showItemQuantity: false,
		showShippingDescription: true,
		showDriverName: false,
		showOrderComment: false,
		showCity: true,
		showRegion: false,
		showDistrict: false,
		showArea: true,
		pageSize: 'A6',
		fontSize: '10px',
		fontWeight: 'regular',
		shipmentNumberBarcodeFormat: 'CODE128',
		orderNumberBarcodeFormat: 'CODE128',
		showDeliveryCompanyLogo: true,
		language: 'engValue',
		templateName: 'template1',
		pageMarginHorizontal: null,
		pageMarginVertical: null,
		shipmentNumberBarcodeFullWidth: true,
		shipmentNumberBarcodeWidth: 1,
		shipmentNumberBarcodeHeight: 30,
		orderNumberBarcodeFullWidth: true,
		orderNumberBarcodeWidth: 1,
		orderNumberBarcodeHeight: 30,
		showSuccessMessage: true,
		showFooterOnTop: false,
		showOtoLogo: undefined,
		showRef1: false,
		showSenderPhoneNumber: true,
		showReceiverPhoneNumber: true,
		showOrderPlatform: false,
		hidePageNumber: false,
		hidePoweredByOto: false,
		numberOfCharactersPerItem: undefined,
		numberOfShownItems: undefined,
		showSortingCode: false
	}
};

export const getPrintSettings = createAsyncThunk('printSettings/fetchAll', async (enc, { getState }) => {
	const { printSettings } = getState();

	const { isSettingsFetchedFromRemote } = printSettings;

	if (isSettingsFetchedFromRemote) {
		return printSettings;
	} else {
		const defaultSettings = structuredClone(printSettings);
		const axios = useAxiosPrivate();

		const response = enc
			? await openAxios.post(PRINT.GET_PRINT_SETTINGS_PUBLIC, { enc })
			: await axios.post(PRINT.GET_PRINT_SETTINGS);
		if (response.status === 200) {
			const { invoicePrintPropJSON, branchPrintPropJSON, awbPrintPropJSON } = response.data;

			const settingsData = {
				invoicePrintPropJSON: {
					...defaultSettings.invoicePrintPropJSON,
					...(invoicePrintPropJSON && JSON.parse(invoicePrintPropJSON))
				},
				branchPrintPropJSON: {
					...defaultSettings.branchPrintPropJSON,
					...(branchPrintPropJSON && JSON.parse(branchPrintPropJSON))
				},
				awbPrintPropJSON: {
					...defaultSettings.awbPrintPropJSON,
					...(awbPrintPropJSON && JSON.parse(awbPrintPropJSON))
				}
			};

			return { ...settingsData, isSettingsFetchedFromRemote: true };
		} else {
			return printSettings;
		}
	}
});

export const setPrintLocale = createAsyncThunk('printSettings/setLocale', async (_payload, { getState }) => {
	const { printSettings } = getState();

	return {
		...printSettings,
		printLocale: _payload ?? 'engValue',
		invoicePrintPropJSON: {
			...printSettings.invoicePrintPropJSON,
			language: _payload ?? 'engValue'
		},
		awbPrintPropJSON: {
			...printSettings.awbPrintPropJSON,
			language: _payload ?? 'engValue'
		},
		branchPrintPropJSON: {
			...printSettings.branchPrintPropJSON,
			language: _payload ?? 'engValue'
		}
	};
});

const deepCopy = (obj) => {
	if (typeof structuredClone === 'function') {
		return structuredClone(obj);
	}
	return JSON.parse(JSON.stringify(obj));
};

const printSettings = createSlice({
	name: 'printSettings',
	initialState,
	reducers: {
		setPrintSettings: (state, { payload }) => ({ ...state, ...deepCopy(payload) }),
		setIsUserSelectedLocale: (state, { payload }) => ({ ...state, isUserSelectedLocale: payload })
	},
	extraReducers: (builder) => {
		builder
			.addCase(getPrintSettings.fulfilled, (state, { payload }) => ({ ...state, ...payload }))
			.addCase(setPrintLocale.fulfilled, (state, { payload }) => ({ ...state, ...payload }));
	}
});

export const { setPrintSettings, setIsUserSelectedLocale } = printSettings.actions;

export default printSettings.reducer;
